/**
 * Device-authorization ("link code") login for the CLI.
 *
 * `futurex login` no longer takes a password in the terminal: it asks the
 * gateway to start a device authorization, shows a short code + a verification
 * link, opens the browser, and polls until the user approves while signed in to
 * the web console. On approval the gateway returns the same session tokens a
 * password login would, which {@link writeCreds} persists — so the rest of the
 * CLI is unchanged.
 */
import { spawn } from 'node:child_process';
import { apiBaseUrl } from './config.js';
import { readCreds, writeCreds, type Credentials } from './creds.js';
import { color, info, line, startSpinner } from './ui.js';

interface DeviceStartData {
  deviceCode: string;
  userCode: string;
  verificationUri: string;
  verificationUriComplete: string;
  expiresIn: number;
  interval: number;
}

interface Envelope<T> {
  success?: boolean;
  data?: T;
  error?: { message?: string };
}

interface TokenData {
  status: 'authorization_pending' | 'denied' | 'expired' | 'approved';
  user?: { email?: string };
  tokens?: { accessToken?: string };
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** Pull the `fim_rt` refresh cookie out of a response's Set-Cookie headers. */
function extractRefreshCookie(res: Response): string | undefined {
  const getSetCookie = (res.headers as { getSetCookie?: () => string[] }).getSetCookie;
  const cookies = typeof getSetCookie === 'function' ? getSetCookie.call(res.headers) : [];
  for (const c of cookies) {
    if (c.startsWith('fim_rt=')) return c.split(';')[0];
  }
  return undefined;
}

/** Best-effort: open `url` in the platform browser. Never throws. */
function openBrowser(url: string): void {
  try {
    const platform = process.platform;
    const [cmd, args] =
      platform === 'win32'
        ? ['cmd', ['/c', 'start', '', url]]
        : platform === 'darwin'
          ? ['open', [url]]
          : ['xdg-open', [url]];
    const child = spawn(cmd, args, { stdio: 'ignore', detached: true });
    child.on('error', () => {});
    child.unref();
  } catch {
    /* headless / no browser — the printed link is the fallback */
  }
}

/**
 * Run the device-authorization login. Prints the code + link, opens the
 * browser, and polls until approval, then persists + returns the credentials.
 * Throws on denial, expiry, or timeout.
 */
export async function deviceLogin(opts: { noBrowser?: boolean } = {}): Promise<Credentials> {
  const apiUrl = apiBaseUrl();

  const startRes = await fetch(`${apiUrl}/api/v1/futurex/device/start`, { method: 'POST' });
  const startJson = (await startRes.json().catch(() => ({}))) as Envelope<DeviceStartData>;
  const start = startJson.data;
  if (!startRes.ok || !start?.deviceCode) {
    throw new Error(startJson.error?.message ?? `could not start sign-in (${startRes.status})`);
  }

  line();
  line(`  ${color.bold('Sign in to FutureX')}`);
  line(`  Open ${color.cyan(start.verificationUri)} and enter this code:`);
  line();
  line(`      ${color.bold(color.magenta(start.userCode))}`);
  line();
  line(color.dim(`  Only approve a code you started here. The link expires in ${Math.round(start.expiresIn / 60)} min.`));
  line();

  if (!opts.noBrowser) {
    info('opening your browser…');
    openBrowser(start.verificationUriComplete);
  }

  const intervalMs = Math.max(1, start.interval) * 1000;
  const deadline = Date.now() + start.expiresIn * 1000;
  const spin = startSpinner('waiting for approval in your browser…');
  try {
    while (Date.now() < deadline) {
      await sleep(intervalMs);
      let tokenJson: Envelope<TokenData>;
      try {
        const res = await fetch(`${apiUrl}/api/v1/futurex/device/token`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ deviceCode: start.deviceCode }),
        });
        tokenJson = (await res.json().catch(() => ({}))) as Envelope<TokenData>;
        const data = tokenJson.data;
        if (data?.status === 'approved') {
          const accessToken = data.tokens?.accessToken;
          if (!accessToken) throw new Error('approval returned no access token');
          const creds: Credentials = {
            apiUrl,
            ...(data.user?.email ? { email: data.user.email } : {}),
            accessToken,
            ...(extractRefreshCookie(res) ? { refreshCookie: extractRefreshCookie(res) } : {}),
          };
          writeCreds(creds);
          return creds;
        }
        if (data?.status === 'denied') throw new Error('sign-in was declined in the browser');
        if (data?.status === 'expired') throw new Error('the code expired — run `futurex login` again');
        // authorization_pending (or a transient non-2xx) → keep polling.
      } catch (err) {
        // A terminal status above is rethrown; transient network blips fall
        // through and we retry on the next tick.
        if (err instanceof Error && /declined|expired|no access token/.test(err.message)) throw err;
      }
    }
  } finally {
    spin.stop();
  }
  throw new Error('sign-in timed out — run `futurex login` again');
}

/**
 * Return stored credentials, or run the device-login flow when absent. The
 * single gate that makes login mandatory: every FutureX command that reaches
 * the gateway calls this first.
 */
export async function ensureLoggedIn(): Promise<Credentials> {
  const existing = readCreds();
  if (existing?.accessToken) return existing;
  info('FutureX requires sign-in.');
  const creds = await deviceLogin();
  info(`signed in as ${creds.email ?? 'your account'}`);
  return creds;
}
